import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import AppIcon from './AppIcon';
import helpers, { assetMapping, isUnified } from '../utils/helpers';
import OrganizationIcon from './OrganizationIcon';
import Tooltip from './Tooltip';

const isAppKind = (appKind) => assetMapping.apps.includes(appKind);
const isPubKind = (pubKind) => assetMapping.publishers.includes(pubKind);
const defaultFallbackKind = (store) => (isUnified(store) ? 'unified_app' : 'app');

const MediaObject = ({
  icon_url,
  icon_url_2x,
  name,
  publisher_name,
  store,
  kind,
  app_id,
  ref_no,
  publisher_id,
  publisher_store,
  style,
  className,
  appNameLink,
  disableLink,
  disablePublisherLink,
  noPublisherData,
  withoutStore,
  appIconSize,
  storeIconClass,
  storeIconOnTop,
  appIconBordered,
  appLinkProps,
  tooltip,
  clickableIcon = false,
  hideHtmlTitle,
  showPubIcon,
  showPubStore,
  fallbackKind,
  appendMediaBody,
  appQuickViewAllowed,
  defaultBrokenNoStoreIcon,
  decorateName,
  onPubQuickViewOpen,
  onAppQuickViewOpen,
}) => {
  const entityKind = kind || fallbackKind(store);
  const EntityNameUrl = appNameLink || `/apps/${store}/${app_id}/performance`;
  const getUrlFor = () => {
    switch (entityKind) {
      case 'unified_app':
        return {
          entityName: EntityNameUrl,
          publisherName: `/publishers/unified/${publisher_id}`,
        };
      case 'publisher':
        return { entityName: `/publishers/${publisher_store || store}/${app_id}` };
      case 'unified_publisher':
        return { entityName: `/publishers/unified/${app_id}` };
      case 'company':
        return { entityName: `/companies/${app_id}` };
      default:
        return {
          entityName: EntityNameUrl,
          publisherName: `/publishers/${publisher_store || store}/${publisher_id}`,
        };
    }
  };
  const withStoreIcon =
    !withoutStore && !['publisher', 'unified_publisher', 'company'].includes(entityKind);
  // TODO: add CQV If needed
  const handleClickPubName = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const pubStore = publisher_store || store;
    onPubQuickViewOpen({
      kind: isUnified(pubStore) ? 'unified_publisher' : 'publisher',
      ref_no: publisher_id || app_id,
      store: pubStore,
    });
  };

  const handleClickAppName = (event) => {
    if (isAppKind(entityKind)) {
      event.preventDefault();
      event.stopPropagation();
      onAppQuickViewOpen({
        icon_url,
        icon_url_2x,
        name,
        publisher_name,
        store,
        kind: entityKind,
        ref_no: ref_no || app_id,
        publisher_id,
        publisher_store,
      });
    } else if (isPubKind(entityKind)) {
      handleClickPubName(event);
    }
  };

  const isPublisherWithStore =
    showPubStore && (entityKind === 'publisher' || entityKind === 'unified_publisher');
  const decoratedName = decorateName ? decorateName(name) : name;

  return (
    <Tooltip id={app_id + entityKind} placement="topLeft" content={tooltip && decoratedName}>
      <div
        className={cx(
          'media-object media-middle',
          {
            'without-store-icon': !withStoreIcon,
            'store-icon-on-top': storeIconOnTop,
          },
          className
        )}
        style={style || {}}
      >
        {withStoreIcon && (
          <i
            className={cx('text-quiet store-icon', helpers.storeIconClasses[store], storeIconClass)}
          />
        )}
        {clickableIcon ? (
          <a
            className={cx(isPublisherWithStore ? 'organization-figure' : 'media-figure')}
            href={getUrlFor().entityName}
            onClick={appQuickViewAllowed && onAppQuickViewOpen ? handleClickAppName : () => {}}
          >
            {icon_url || isAppKind(entityKind) ? (
              <AppIcon
                alt={name}
                url={icon_url}
                url2x={icon_url_2x}
                store={store}
                size={appIconSize}
                withBorder={appIconBordered}
                hideHtmlTitle={hideHtmlTitle}
              />
            ) : (
              <OrganizationIcon
                kind={entityKind}
                store={store}
                showPubIcon={showPubIcon}
                showPubStore={showPubStore}
              />
            )}
          </a>
        ) : (
          <div
            className={cx(isPublisherWithStore ? 'organization-figure' : 'media-figure', {
              'organization-figure': !icon_url,
            })}
            style={{ minWidth: appIconSize, maxWidth: isPublisherWithStore ? null : appIconSize }}
          >
            {icon_url || isAppKind(entityKind) ? (
              <AppIcon
                alt={name}
                url={icon_url}
                url2x={icon_url_2x}
                store={store}
                size={appIconSize}
                withBorder={appIconBordered}
                defaultBrokenNoStoreIcon={defaultBrokenNoStoreIcon}
              />
            ) : (
              <OrganizationIcon
                kind={entityKind}
                store={store}
                showPubIcon={showPubIcon}
                showPubStore={showPubStore}
              />
            )}
          </div>
        )}
        <div className="media-body">
          {disableLink ? (
            <p className="d-block app-name text-truncate">{decoratedName}</p>
          ) : (
            <a
              className="d-block app-name text-truncate cursor-pointer"
              href={getUrlFor().entityName}
              onClick={appQuickViewAllowed && onAppQuickViewOpen ? handleClickAppName : () => {}}
              {...(appLinkProps || {})}
            >
              {decorateName ? decoratedName : helpers.decodeEntities(name || '')}
            </a>
          )}
          {!noPublisherData &&
            publisher_name &&
            (disablePublisherLink || !publisher_id ? (
              <p className="d-block app-publisher text-truncate text-xxxs">{publisher_name}</p>
            ) : (
              <a
                className="d-block app-publisher text-truncate text-xxxs"
                href={getUrlFor().publisherName}
                onClick={appQuickViewAllowed && onPubQuickViewOpen ? handleClickPubName : () => {}}
              >
                {helpers.decodeEntities(publisher_name)}
              </a>
            ))}
          {appendMediaBody}
        </div>
      </div>
    </Tooltip>
  );
};

MediaObject.propTypes = {
  name: PropTypes.string.isRequired,
  appNameLink: PropTypes.string,
  publisher_name: PropTypes.string,
  publisher_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  publisher_store: PropTypes.string,
  store: PropTypes.string,
  kind: PropTypes.string,
  app_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ref_no: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon_url: PropTypes.string,
  icon_url_2x: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  disableLink: PropTypes.bool,
  disablePublisherLink: PropTypes.bool,
  noPublisherData: PropTypes.bool,
  withoutStore: PropTypes.bool,
  appIconSize: PropTypes.number,
  storeIconClass: PropTypes.string,
  appIconBordered: PropTypes.bool,
  appLinkProps: PropTypes.object,
  tooltip: PropTypes.bool,
  clickableIcon: PropTypes.bool,
  hideHtmlTitle: PropTypes.bool,
  showPubIcon: PropTypes.bool,
  showPubStore: PropTypes.bool,
  appQuickViewAllowed: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  fallbackKind: PropTypes.func,
  appendMediaBody: PropTypes.node,
  defaultBrokenNoStoreIcon: PropTypes.string,
  decorateName: PropTypes.func,
  onPubQuickViewOpen: PropTypes.func,
  onAppQuickViewOpen: PropTypes.func,
};

MediaObject.defaultProps = {
  disableLink: false,
  disablePublisherLink: false,
  noPublisherData: false,
  appIconSize: 32,
  storeIconClass: '',
  appIconBordered: false,
  tooltip: false,
  fallbackKind: defaultFallbackKind,
};

export default MediaObject;
